<template>
    <div
        class="toast-item w-full max-w-md overflow-hidden rounded-2xl shadow-xl transition-all duration-300 ease-out"
        :class="[typeClasses]"
    >
        <!-- Colored top bar -->
        <div class="h-1.5" :class="topBarClasses" />

        <div class="p-5">
            <div class="flex items-start gap-4">
                <!-- Icon container with background -->
                <div class="shrink-0 rounded-xl p-2.5" :class="iconBgClasses">
                    <component :is="iconComponent" class="size-6" :class="iconClasses" />
                </div>

                <!-- Content -->
                <div class="min-w-0 flex-1 pt-0.5">
                    <p class="text-base font-semibold leading-tight" :class="titleClasses">
                        {{ title }}
                    </p>
                    <p v-if="message" class="mt-1.5 text-sm leading-relaxed" :class="messageClasses">
                        {{ message }}
                    </p>
                </div>

                <!-- Close button -->
                <button
                    @click="close"
                    class="shrink-0 rounded-lg p-1.5 transition-all duration-200 hover:scale-110"
                    :class="closeButtonClasses"
                >
                    <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
interface Props {
    type?: 'success' | 'error' | 'info' | 'warning'
    title?: string
    message?: string
    duration?: number
    isVisible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    type: 'info',
    title: '',
    message: '',
    duration: 5000,
    isVisible: false
})

const emit = defineEmits<{
    close: []
}>()

// Auto close after duration
let timeoutId: NodeJS.Timeout | null = null

watchEffect(() => {
    if (props.isVisible && props.duration > 0) {
        timeoutId = setTimeout(() => {
            emit('close')
        }, props.duration)
    } else if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
    }
})

onUnmounted(() => {
    if (timeoutId) {
        clearTimeout(timeoutId)
    }
})

function close() {
    emit('close')
}

// Computed styles based on type
const typeClasses = computed(() => {
    switch (props.type) {
        case 'success':
            return 'bg-white dark:bg-surface-card border border-primary-200 dark:border-primary-800'
        case 'error':
            return 'bg-white dark:bg-surface-card border border-red-200 dark:border-red-800'
        case 'warning':
            return 'bg-white dark:bg-surface-card border border-amber-200 dark:border-amber-800'
        case 'info':
        default:
            return 'bg-white dark:bg-surface-card border border-blue-200 dark:border-blue-800'
    }
})

const topBarClasses = computed(() => {
    switch (props.type) {
        case 'success':
            return 'bg-gradient-to-r from-primary-500 to-primary-600'
        case 'error':
            return 'bg-gradient-to-r from-red-500 to-red-600'
        case 'warning':
            return 'bg-gradient-to-r from-amber-400 to-amber-500'
        case 'info':
        default:
            return 'bg-gradient-to-r from-blue-500 to-blue-600'
    }
})

const iconBgClasses = computed(() => {
    switch (props.type) {
        case 'success':
            return 'bg-primary-50 dark:bg-primary-900/30'
        case 'error':
            return 'bg-red-50 dark:bg-red-900/30'
        case 'warning':
            return 'bg-amber-50 dark:bg-amber-900/30'
        case 'info':
        default:
            return 'bg-blue-50 dark:bg-blue-900/30'
    }
})

const titleClasses = computed(() => {
    switch (props.type) {
        case 'success':
            return 'text-primary-900 dark:text-primary-100'
        case 'error':
            return 'text-red-900 dark:text-red-100'
        case 'warning':
            return 'text-amber-900 dark:text-amber-100'
        case 'info':
        default:
            return 'text-blue-900 dark:text-blue-100'
    }
})

const messageClasses = computed(() => {
    switch (props.type) {
        case 'success':
            return 'text-primary-700 dark:text-primary-300'
        case 'error':
            return 'text-red-700 dark:text-red-300'
        case 'warning':
            return 'text-amber-700 dark:text-amber-300'
        case 'info':
        default:
            return 'text-blue-700 dark:text-blue-300'
    }
})

const iconClasses = computed(() => {
    switch (props.type) {
        case 'success':
            return 'text-primary-600 dark:text-primary-400'
        case 'error':
            return 'text-red-600 dark:text-red-400'
        case 'warning':
            return 'text-amber-600 dark:text-amber-400'
        case 'info':
        default:
            return 'text-blue-600 dark:text-blue-400'
    }
})

const closeButtonClasses = computed(() => {
    switch (props.type) {
        case 'success':
            return 'text-primary-400 hover:text-primary-600 hover:bg-primary-50 dark:text-primary-500 dark:hover:text-primary-300 dark:hover:bg-primary-900/30'
        case 'error':
            return 'text-red-400 hover:text-red-600 hover:bg-red-50 dark:text-red-500 dark:hover:text-red-300 dark:hover:bg-red-900/30'
        case 'warning':
            return 'text-amber-400 hover:text-amber-600 hover:bg-amber-50 dark:text-amber-500 dark:hover:text-amber-300 dark:hover:bg-amber-900/30'
        case 'info':
        default:
            return 'text-blue-400 hover:text-blue-600 hover:bg-blue-50 dark:text-blue-500 dark:hover:text-blue-300 dark:hover:bg-blue-900/30'
    }
})

const iconComponent = computed(() => {
    switch (props.type) {
        case 'success':
            return 'CheckCircleIcon'
        case 'error':
            return 'ExclamationCircleIcon'
        case 'warning':
            return 'ExclamationTriangleIcon'
        case 'info':
        default:
            return 'InformationCircleIcon'
    }
})
</script>

<script lang="ts">
// Simple icon components since we don't have a full icon library
const CheckCircleIcon = {
    template: `
        <svg fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
    `
}

const ExclamationCircleIcon = {
    template: `
        <svg fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
    `
}

const ExclamationTriangleIcon = {
    template: `
        <svg fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
    `
}

const InformationCircleIcon = {
    template: `
        <svg fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
        </svg>
    `
}

export default {
    components: {
        CheckCircleIcon,
        ExclamationCircleIcon,
        ExclamationTriangleIcon,
        InformationCircleIcon
    }
}
</script>
